/**
 * Camera direction during a match (GDD section 4):
 * - aiming: free scrolling, the camera never comes back on its own (◎ recentres);
 * - after a shot: follows the newest projectile with a slight lag (15 % of the gap per frame) and a lead
 *   in the direction of motion;
 * - on impact: stays on the area and follows the units thrown away until the end of the turn;
 * - next turn: glides in 0.5 s to the active player's unit.
 * The player may take over at any time (right-drag); following resumes at the next event.
 */
import { FP, PFP, UNIT_H, UNIT_W, type GameEvent, type GameState } from '@invaders/game';
import type { Camera } from '../shared/camera';

const CATCH_UP = 0.15;
/** Lead, in ticks of the projectile's velocity. */
const LEAD_TICKS = 18;
const GLIDE_S = 0.5;

export class Director {
  private following = false;
  private manual = false;
  private impact: [number, number] | null = null;
  private glide: { fx: number; fy: number; tx: number; ty: number; t: number } | null = null;

  /** The player moved the camera by hand. */
  takeOver(): void {
    this.manual = true;
    this.glide = null;
  }

  onEvents(events: readonly GameEvent[]): void {
    for (const e of events) {
      if (e.type === 'launch') { this.following = true; this.manual = false; this.impact = null; }
      else if (e.type === 'explosion') { this.impact = [e.x, e.y]; this.manual = false; }
      else if (e.type === 'death') this.manual = false;
    }
  }

  /** Start of a turn: glide to (x, y), the centre of the unit to play. */
  glideTo(cam: Camera, x: number, y: number): void {
    this.following = false;
    this.manual = false;
    this.impact = null;
    this.glide = { fx: cam.x + cam.spanW / 2, fy: cam.y + cam.spanH / 2, tx: x, ty: y, t: 0 };
  }

  update(dt: number, state: GameState, cam: Camera): void {
    if (this.glide) {
      const g = this.glide;
      g.t = Math.min(1, g.t + dt / GLIDE_S);
      const k = g.t < 0.5 ? 2 * g.t * g.t : 1 - 2 * (1 - g.t) * (1 - g.t);
      cam.centerOn(g.fx + (g.tx - g.fx) * k, g.fy + (g.ty - g.fy) * k);
      if (g.t >= 1) this.glide = null;
      return;
    }
    if (!this.following || this.manual) return;
    let target: [number, number] | null = null;
    const p = state.projectiles[state.projectiles.length - 1];
    const surge = state.surges[state.surges.length - 1];
    if (p) {
      target = [(p.x + p.vx * LEAD_TICKS) / PFP, (p.y + p.vy * LEAD_TICKS) / PFP];
    } else if (surge) {
      target = [surge.x + surge.dir * 40, surge.floor - surge.height];
    } else {
      const moving = state.units.filter((u) => u.alive && (u.vx !== 0 || u.vy !== 0));
      if (moving.length > 0) {
        target = [
          moving.reduce((s, u) => s + u.x / FP + UNIT_W / 2, 0) / moving.length,
          moving.reduce((s, u) => s + u.y / FP + UNIT_H / 2, 0) / moving.length,
        ];
      } else target = this.impact;
    }
    if (!target) return;
    const k = 1 - Math.pow(1 - CATCH_UP, dt * 60);
    const cx = cam.x + cam.spanW / 2;
    const cy = cam.y + cam.spanH / 2;
    cam.centerOn(cx + (target[0] - cx) * k, cy + (target[1] - cy) * k);
  }
}
